import { useEffect, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { queryClient } from "@/lib/query-client";
import { registerPushDevice } from "@/services/push-device-api";
import { useAppStore } from "@/store/app-store";
import { NOTIFICATION_SOUNDS, getSelectedSoundId, playAlertSound } from "@/lib/notification-sound";

// Channel ids we shipped before the per-sound channels below. An Android
// channel's sound is fixed at creation, so old channels are deleted and
// recreated rather than mutated.
const LEGACY_CHANNEL_IDS = ["booking_alerts", "booking_alerts_v2"];

const setupNotificationChannels = async () => {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    for (const id of LEGACY_CHANNEL_IDS) {
      await PushNotifications.deleteChannel({ id }).catch(() => undefined);
    }
    for (const sound of NOTIFICATION_SOUNDS) {
      await PushNotifications.createChannel({
        id: sound.channelId,
        name: `Booking alerts – ${sound.label}`,
        description: "New bookings, confirmations and cancellations",
        importance: 5,
        visibility: 1,
        sound: sound.file,
        vibration: true,
        lights: true,
      });
    }
  } catch (error) {
    console.error("Failed to create notification channels", error);
  }
};

export function PushNotificationProvider({ children }: { children: ReactNode }) {
  const token = useAppStore((state) => state.token);
  const setPushToken = useAppStore((state) => state.setPushToken);

  useEffect(() => {
    if (!token || !Capacitor.isNativePlatform()) return;
    let cancelled = false;

    const setupPush = async () => {
      try {
        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== "granted" || cancelled) return;

        await setupNotificationChannels();
        await PushNotifications.register();

        await PushNotifications.removeAllListeners();
        await PushNotifications.addListener("registration", async ({ value }) => {
          if (cancelled) return;
          setPushToken(value);
          try {
            await registerPushDevice({
              token: value,
              platform: Capacitor.getPlatform(),
              sound: getSelectedSoundId(),
            });
            console.log("Push device token registered");
          } catch (error) {
            console.error("Failed to register push device token", error);
          }
        });
        await PushNotifications.addListener("registrationError", (error) => {
          console.error("Push registration failed", error);
        });
        await PushNotifications.addListener("pushNotificationReceived", (notification) => {
          // Foreground pushes don't raise a system notification, so play the
          // user's chosen alert tone to match the background channel sound -
          // owners only, and not for booking alarms (BookingAlertService rings those).
          const type = notification.data?.type;
          const isAlarm = type === "booking_alert" || type === "booking_alert_stop";
          if (!isAlarm && useAppStore.getState().user?.role === "OWNER") playAlertSound();
          void queryClient.invalidateQueries({ queryKey: ["notifications"] });
          void queryClient.invalidateQueries({ queryKey: ["bookings"] });
          void queryClient.invalidateQueries({ queryKey: ["pending-bookings"] });
        });
        await PushNotifications.addListener("pushNotificationActionPerformed", () => {
          void queryClient.invalidateQueries({ queryKey: ["notifications"] });
          void queryClient.invalidateQueries({ queryKey: ["bookings"] });
          void queryClient.invalidateQueries({ queryKey: ["pending-bookings"] });
        });
      } catch (error) {
        // Push registration needs a real google-services.json wired into the native
        // build (Firebase project console) — without it this rejects instead of
        // taking the app down, so the rest of the app keeps working.
        console.error("Push notification setup failed", error);
      }
    };

    void setupPush();
    return () => {
      cancelled = true;
      void PushNotifications.removeAllListeners();
    };
  }, [token, setPushToken]);

  return <>{children}</>;
}
